import { Link } from '@tanstack/react-router'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { StatCell } from '@workspace/ui/components/stat-cell'
import { useState } from 'react'
import { useAdminObserverAlertsQuery, useAdminObserversQuery } from '../hooks/use-admin-api'

const PAGE_SIZE = 25

const STATUS_FILTERS = [
  { label: 'All', value: undefined },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Waiting', value: 'WAITING' },
  { label: 'Fired', value: 'FIRED' },
  { label: 'Ended', value: 'ENDED_BY_INVESTOR' },
  { label: 'Removed by founder', value: 'REMOVED_BY_FOUNDER' },
] as const

type StatusFilter = (typeof STATUS_FILTERS)[number]['value']
type Classification = 'pending' | 'failed' | undefined

type Item = NonNullable<ReturnType<typeof useAdminObserversQuery>['data']>['data']['items'][number]

function formatDate(value: Date | string | null) {
  if (!value) return '-'
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function statusLabel(item: Item) {
  if (item.status === 'WAITING') {
    return item.waitingReason === 'CHECK' ? 'Waiting for the check' : 'Waiting for a place'
  }
  return item.status.replaceAll('_', ' ').toLowerCase()
}

function formatTime(value: Date | string | null) {
  if (!value) return '-'
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** What a follow's alerts quoted: the founder's own words, date and source, linked to the stored fact. */
function FiredStatements({ observerId }: { observerId: string }) {
  const { data, isLoading } = useAdminObserverAlertsQuery(observerId, true)
  if (isLoading) return <CellText>Loading...</CellText>
  const alerts = data?.data.alerts ?? []
  const startupId = data?.data.startupId
  if (alerts.length === 0) return <CellText>No alert has fired yet.</CellText>
  return (
    <div className="space-y-3 p-2">
      {alerts.map((alert) => (
        <div key={alert.id} className="space-y-1.5 border-t border-border pt-2 first:border-t-0">
          <CellText className="block text-xs">
            Fired {formatTime(alert.createdAt)} · emailed {formatTime(alert.emailedAt)} · read in
            app {formatTime(alert.readInAppAt)} · seen in journal{' '}
            {formatTime(alert.seenInJournalAt)}
          </CellText>
          {alert.statements.map((statement) => (
            <div key={`${alert.id}-${statement.quote}`} className="rounded bg-muted p-2 text-sm">
              <p className="break-words">&ldquo;{statement.quote}&rdquo;</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {statement.source} · {statement.date}
                {statement.kinds.length > 0
                  ? ` · ${statement.kinds.map((k) => k.replaceAll('_', ' ')).join(', ')}`
                  : ''}
                {statement.claimId && startupId ? (
                  <>
                    {' · '}
                    <Link
                      to="/admin/startups/$id"
                      params={{ id: startupId }}
                      search={{ tab: 'facts' }}
                      className="text-primary hover:underline"
                    >
                      fact {statement.claimId}
                    </Link>
                  </>
                ) : null}
              </p>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

export function ObserversTable({
  investorId,
  showSummary = true,
}: {
  investorId?: string
  showSummary?: boolean
}) {
  const [status, setStatus] = useState<StatusFilter>(undefined)
  const [classification, setClassification] = useState<Classification>(undefined)
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState<string | null>(null)
  const query = useAdminObserversQuery({
    status,
    classification,
    investorId,
    page,
    limit: PAGE_SIZE,
  })
  const data = query.data?.data
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1

  const columns: TableColumn<Item>[] = [
    {
      header: 'Investor',
      render: (item) => (
        <div className="space-y-0.5">
          <Link
            to="/admin/users/$id"
            params={{ id: item.investor.userId }}
            className="text-sm font-medium text-primary hover:underline"
          >
            {item.investor.name ?? item.investor.email}
          </Link>
          {item.firm ? (
            <Link
              to="/admin/investors/$id"
              params={{ id: item.firm.id }}
              className="block text-xs text-primary hover:underline"
            >
              {item.firm.name} · {item.firm.planTier.toLowerCase()}
            </Link>
          ) : (
            <CellText className="block text-xs">No firm</CellText>
          )}
        </div>
      ),
    },
    {
      header: 'Startup',
      render: (item) => (
        <Link
          to="/admin/startups/$id"
          params={{ id: item.startup.id }}
          className="text-sm font-medium text-primary hover:underline"
        >
          {item.startup.name}
        </Link>
      ),
    },
    {
      header: 'Follow',
      width: '200px',
      render: (item) => (
        <div className="space-y-0.5">
          <Badge variant={item.status === 'ACTIVE' ? 'secondary' : 'outline'}>
            {statusLabel(item)}
          </Badge>
          <CellText className="block text-xs">
            {item.mode === 'ALL_UPDATES' ? 'Every major update' : 'A condition'} ·{' '}
            {item.createdVia.replaceAll('_', ' ').toLowerCase()} · {item.authoredBy.toLowerCase()}
          </CellText>
        </div>
      ),
    },
    {
      header: 'Classification',
      width: '220px',
      render: (item) =>
        item.conditions.length === 0 ? (
          <CellText>None</CellText>
        ) : (
          <div className="space-y-1">
            {item.conditions.map((condition) => (
              <div key={condition.key} className="flex flex-wrap items-center gap-1">
                <Badge variant={condition.classifyStatus === 'FAILED' ? 'destructive' : 'outline'}>
                  {condition.classifyStatus.toLowerCase()}
                </Badge>
                <CellText className="text-xs">
                  {condition.kinds
                    .map((kind) => kind.replaceAll('_', ' ').toLowerCase())
                    .join(', ')}
                </CellText>
              </div>
            ))}
          </div>
        ),
    },
    {
      header: 'Alerts',
      width: '170px',
      render: (item) =>
        item.alerts.count === 0 ? (
          <CellText>None</CellText>
        ) : (
          <div className="space-y-0.5">
            <CellText className="block text-xs">
              {item.alerts.count} · last {formatDate(item.alerts.lastAt)}
            </CellText>
            <CellText className="block text-xs">
              {item.alerts.emailed} emailed · {item.alerts.readInApp} read in app ·{' '}
              {item.alerts.seenInJournal} in journal
            </CellText>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setOpen(open === item.observerId ? null : item.observerId)}
            >
              {open === item.observerId ? 'Hide what fired' : 'Show what fired'}
            </Button>
          </div>
        ),
    },
    {
      header: 'Followed',
      width: '130px',
      render: (item) => <CellText>{formatDate(item.followedAt)}</CellText>,
    },
    {
      header: 'Last checked',
      width: '130px',
      render: (item) => <CellText>{formatDate(item.lastCheckedAt)}</CellText>,
    },
  ]

  return (
    <div className="space-y-6">
      {showSummary && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <StatCell size="sm" value={data?.summary.active ?? '-'} label="Active" />
          <StatCell
            size="sm"
            value={data?.summary.waitingLimit ?? '-'}
            label="Waiting for a place"
          />
          <StatCell
            size="sm"
            value={data?.summary.waitingCheck ?? '-'}
            label="Waiting for the check"
          />
          <StatCell size="sm" value={data?.summary.fired ?? '-'} label="Fired" />
          <StatCell
            size="sm"
            value={data?.summary.classificationPending ?? '-'}
            label="Read pending"
            accent={(data?.summary.classificationPending ?? 0) > 0}
          />
          <StatCell
            size="sm"
            value={data?.summary.classificationFailed ?? '-'}
            label="Read failed"
            accent={(data?.summary.classificationFailed ?? 0) > 0}
          />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((filter) => (
          <Button
            key={filter.label}
            size="sm"
            variant={status === filter.value ? 'default' : 'outline'}
            onClick={() => {
              setStatus(filter.value)
              setPage(1)
            }}
          >
            {filter.label}
          </Button>
        ))}
        {(['pending', 'failed'] as const).map((value) => (
          <Button
            key={value}
            size="sm"
            variant={classification === value ? 'default' : 'outline'}
            onClick={() => {
              setClassification(classification === value ? undefined : value)
              setPage(1)
            }}
          >
            Read {value}
          </Button>
        ))}
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        isLoading={query.isLoading}
        emptyMessage="No follows match."
        keyExtractor={(item) => item.observerId}
        minWidth="1000px"
        renderExpanded={(item) =>
          open === item.observerId ? <FiredStatements observerId={item.observerId} /> : null
        }
      />

      <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
          Previous
        </Button>
        <span>
          Page {page} of {totalPages}
        </span>
        <Button
          size="sm"
          variant="outline"
          disabled={page >= totalPages}
          onClick={() => setPage(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
